import React, { Fragment } from 'react'
import styles from './ShareModal.scss'
import { ModalOverlay } from 'common/styleguide/ModalOverlay'
import { CTA } from 'common/styleguide/CTA'
import { CircleSpinner } from 'common/spinners'
import { Icon } from 'common/Icon'
import { urls } from 'lib/urls'
import fetchJSON from 'lib/fetchJSON'
import { copyToClipboard } from 'lib/copyToClipboard'
import { Msg, fm, commonVerbs, commonActions, commonMessages } from 'lib/localization'
import { injectIntl, defineMessages } from 'react-intl'

export const shareMessages = defineMessages({
  emailAddress: {
    id: 'ShareDialog.emailAddress',
    defaultMessage: 'Email address to share with'
  },
  emailLink: {
    id: 'ShareDialog.emailLink',
    defaultMessage: 'Email Link'
  },
  linkSent: {
    id: 'ShareDialog.linkSent',
    defaultMessage: 'Link Sent'
  },
  noEmail: {
    id: 'ShareDialog.noEmail',
    defaultMessage: 'Please provide a valid email address'
  },
  shareOn: {
    id: 'ShareDialog.shareOn',
    defaultMessage: 'Share on {service}',
    description: 'An action to share an asset on a site or by email'
  }
})

export const SHARE_MODAL = 'SHARE_MODAL'

// share via email params:
// POST /share.json with
// share[model]: route
// share[id]: 27176252
// share[url]: https://ridewithgps.com/routes/27176252
// share[email]: qrohlf@gmail.com

const EMBEDDABLES = ['route', 'trip', 'event']

class _ShareModal extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      interaction: null,
      copySuccess: null
    }
  }

  onClickEmail = (e) => {
    e.preventDefault()
    this.setState({ interaction: 'email_input' })
  }

  onClickCopy = (e) => {
    const { objectUrl } = this.props
    e.preventDefault()
    this.setState({ copySuccess: false }, () => {
      copyToClipboard('https://ridewithgps.com' + objectUrl)
        .then(() => this.setState({ copySuccess: true }))
    })
  }

  render () {
    const { onRequestClose, objectType, objectId, objectTitle, objectUrl, privacyCode, overlayTitle, intl } = this.props
    const { interaction, copySuccess } = this.state

    if (interaction === 'email_input') {
      return <ModalOverlay
        title={<Fragment><Msg m={commonVerbs.share} /> <strong>{objectTitle}</strong></Fragment>}
        maxWidth='400px'
        onRequestClose={onRequestClose}>
        <EmailShare type={objectType} id={objectId} url={objectUrl} intl={intl} />
      </ModalOverlay>
    }

    return (
      <ModalOverlay
        title={overlayTitle || <Fragment><Msg m={commonVerbs.share} /> <strong>{objectTitle}</strong></Fragment>}
        maxWidth='400px'
        onRequestClose={onRequestClose}>
        <div className={styles.ShareModal}>

          <div className={styles.shareIcons}>
            <a
              className={styles.icon}
              target='_blank'
              rel='noopener noreferrer'
              href={`https://facebook.com/sharer.php?u=${encodeURIComponent('https://ridewithgps.com' + objectUrl)}&t=${encodeURIComponent(objectTitle)}`}>
              <Icon i='facebook-icon' title={fm(intl, shareMessages.shareOn, { service: 'Facebook' })} />
            </a>
            <a
              className={styles.icon}
              target='_blank'
              rel='noopener noreferrer'
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('https://ridewithgps.com' + objectUrl)}`}>
              <Icon i='twitter-icon' title={fm(intl, shareMessages.shareOn, { service: 'Twitter' })} />
            </a>
            <a className={styles.icon} href='#' onClick={this.onClickEmail}>
              <Icon i={'email-icon'} />
            </a>
          </div>

          <CTA secondary={!copySuccess} margins onClick={this.onClickCopy}>
            {copySuccess
              ? <Msg m={commonActions.copied} />
              : <React.Fragment><Icon i={'md-link'} size={20} className={styles.ctaIcon} /> <Msg m={commonActions.copyLink} /></React.Fragment>}
          </CTA>

          {EMBEDDABLES.includes(objectType) && objectId &&
            <CTA secondary margins target='_blank' href={urls.embeds.new(objectId, objectType, privacyCode)}>
              <Icon i='md-code' size={20} className={styles.ctaIcon} /> <Msg m={commonActions.getCustomEmbed} />
            </CTA>}
        </div>
      </ModalOverlay>
    )
  }
}

export const ShareModal = injectIntl(_ShareModal)

class EmailShare extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      email: '',
      shareSuccess: null,
      error: null,
      sharePending: false
    }
  }

  onChangeEmail = (e) => {
    this.setState({ email: e.target.value })
  }

  onSubmit = (e) => {
    e.preventDefault()
    const { id, type, url, intl } = this.props
    const { email, sharePending } = this.state
    if (sharePending) { return }
    if (!email) {
      this.setState({ sharePending: false, error: fm(intl, shareMessages.noEmail) })
      return
    }
    const shared = {
      id,
      url: 'https://ridewithgps.com' + url,
      email
    }

    const share = Object.assign(shared,
      type === 'collections'
        ? { scope: type }
        : { model: type }
    )
    this.setState({ sharePending: true })
    fetchJSON('/share', { share })
      .then(() => {
        this.setState({ sharePending: false, shareSuccess: true, error: null })
      })
      .catch(() => {
        this.setState({ sharePending: false, shareSuccess: false, error: fm(intl, commonMessages.shareErrorMessage) })
      })
  }

  render () {
    const { email, error, sharePending, shareSuccess } = this.state

    return (
      <div className={styles.EmailShare}>
        {error && <p>{error}</p>}
        <form onSubmit={this.onSubmit}>
          <label><Msg m={shareMessages.emailAddress} /></label>
          <input type='email' value={email} onChange={this.onChangeEmail} />
          {!shareSuccess && <CTA button type='submit'>
            {sharePending ? <CircleSpinner size={14} /> : <Msg m={shareMessages.emailLink} />}
          </CTA>}
          {shareSuccess && <CTA disabled><Msg m={shareMessages.linkSent} /></CTA>}
        </form>
      </div>
    )
  }
}
